import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchSceneAudio } from "@/lib/api";
import { trackAudio, devLog } from "@/lib/audio";

// Fetches and plays the in-character scene audio for a decision.
// Handles auto-narrate, prefetch of the next scene, and cleanup on change.
export function useSceneAudio(scenarioId, decisionId, autoNarrate, nextDecisionId) {
  const [audioState, setAudioState] = useState("idle");
  const audioRef = useRef(null);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    setAudioState("idle");
  }, []);

  useEffect(() => {
    setAudioState("idle");
    return stop;
  }, [decisionId, stop]);

  useEffect(() => {
    if (!autoNarrate) return undefined;
    let cancelled = false;
    (async () => {
      setAudioState("loading");
      try {
        const url = await fetchSceneAudio(scenarioId, decisionId);
        if (cancelled) return;
        const a = trackAudio(new Audio(url));
        audioRef.current = a;
        a.onended = () => setAudioState("idle");
        await a.play();
        if (!cancelled) setAudioState("playing");
      } catch (err) {
        devLog("Auto-narrate failed:", err);
        if (!cancelled) setAudioState("idle");
      }
    })();
    if (nextDecisionId) {
      fetchSceneAudio(scenarioId, nextDecisionId).catch(() => {});
    }
    return () => {
      cancelled = true;
      stop();
    };
  }, [decisionId, autoNarrate, scenarioId, nextDecisionId, stop]);

  const toggleSceneAudio = async () => {
    if (audioState === "playing") {
      stop();
      return;
    }
    if (audioState === "loading") return;
    setAudioState("loading");
    try {
      const url = await fetchSceneAudio(scenarioId, decisionId);
      const a = trackAudio(new Audio(url));
      audioRef.current = a;
      a.onended = () => setAudioState("idle");
      await a.play();
      setAudioState("playing");
    } catch (err) {
      devLog("Scene audio failed:", err);
      setAudioState("idle");
      toast.error("Scene audio is unavailable right now.");
    }
  };

  return { audioState, toggleSceneAudio };
}
